import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Radio, Calendar as CalendarIcon, Clock, ChevronLeft, Play, Pause, Music2 } from 'lucide-react';
import { usePlayer } from '@/contexts/PlayerContext';
import { track } from '@/lib/analytics';
import AdSlot from '@/components/AdSlot';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

const getShowImageSrc = (show) => {
  if (!show) return null;
  if (show.image_data && show.image_data.startsWith('data:')) return show.image_data;
  if (show.image_storage_path) return `${API}/show-image/${show.id}`;
  if (show.image_url && !show.image_url.startsWith('__storage__') && show.image_url.startsWith('http')) return show.image_url;
  return null;
};

const getHostImageSrc = (host) => {
  if (!host) return null;
  if (host.image_data && host.image_data.startsWith('data:')) return host.image_data;
  if (host.image_storage_path) return `${API}/host-image/${host.id}`;
  if (host.image_url && !host.image_url.startsWith('__storage__')) return host.image_url;
  return null;
};

export default function ShowDetailsPage() {
  const { showId } = useParams();
  const navigate = useNavigate();
  const { radioMode, isPlaying, startRadio, togglePlay } = usePlayer();
  const [show, setShow] = useState(null);
  const [host, setHost] = useState(null);
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setNotFound(false);
    (async () => {
      try {
        const showRes = await axios.get(`${API}/shows/${showId}`);
        if (!active) return;
        setShow(showRes.data);
        const [schedRes, hostsRes] = await Promise.all([
          axios.get(`${API}/schedule`),
          axios.get(`${API}/hosts`),
        ]);
        if (!active) return;
        const schedule = schedRes.data.schedule || [];
        setEntries(
          schedule
            .filter(e => e.show_id === showId)
            .sort((a, b) => a.day_of_week - b.day_of_week || a.start_time.localeCompare(b.start_time))
        );
        const hosts = hostsRes.data.hosts || [];
        setHost(hosts.find(h => h.id === showRes.data.host_id) || null);
      } catch (e) {
        if (!active) return;
        if (e?.response?.status === 404) setNotFound(true);
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [showId]);

  if (loading) {
    return (
      <div data-testid="show-details-loading" className="min-h-screen flex items-center justify-center">
        <div className="w-10 h-10 border-2 border-[#D4AF37]/30 border-t-[#D4AF37] rounded-full animate-spin" />
      </div>
    );
  }

  if (notFound || !show) {
    return (
      <div data-testid="show-details-missing" className="min-h-screen flex flex-col items-center justify-center px-4 text-center">
        <Radio size={48} className="text-[#D4AF37]/40 mb-4" />
        <h2 className="text-2xl text-white font-semibold mb-2">Show not found</h2>
        <p className="text-[#94A3B8] mb-6">The show you’re looking for may have been removed.</p>
        <Link to="/shows" className="text-[#D4AF37] font-medium hover:text-[#E8C24D] inline-flex items-center gap-1">
          <ChevronLeft size={16} /> Back to all shows
        </Link>
      </div>
    );
  }

  const img = getShowImageSrc(show);
  const hostImg = getHostImageSrc(host);

  return (
    <div data-testid="show-details-page" className="pb-28 min-h-screen">
      {/* Hero */}
      <div className="relative overflow-hidden">
        <div className="absolute inset-0">
          {img ? (
            <img src={img} alt={show.name} className="w-full h-full object-cover opacity-30" />
          ) : (
            <div className="w-full h-full bg-gradient-to-br from-[#D4AF37]/20 to-[#991B1B]/20" />
          )}
          <div className="absolute inset-0 bg-gradient-to-b from-[#0F1115]/60 via-[#0F1115]/80 to-[#0F1115]" />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24">
          <button
            onClick={() => navigate(-1)}
            data-testid="show-details-back"
            className="inline-flex items-center gap-1 text-[#94A3B8] hover:text-white text-sm mb-6 transition-colors"
          >
            <ChevronLeft size={16} /> Back
          </button>

          <div className="grid lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-7">
              {show.genre && (
                <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#D4AF37] mb-3">{show.genre}</p>
              )}
              <h1 className="text-4xl sm:text-5xl lg:text-6xl tracking-tighter font-semibold text-white mb-5" style={{ fontFamily: 'Clash Display, sans-serif' }}>
                {show.name}
              </h1>
              {show.description && (
                <p className="text-base sm:text-lg text-[#94A3B8] leading-relaxed max-w-2xl mb-8">
                  {show.description}
                </p>
              )}

              <div className="flex flex-wrap gap-4">
                <button
                  data-testid="show-details-listen-live"
                  onClick={() => {
                    if (radioMode && isPlaying) togglePlay();
                    else { startRadio(); track('listen_live', { source: 'show_details', show_id: showId }); }
                  }}
                  className="bg-[#D4AF37] text-[#0F1115] font-semibold hover:bg-[#E8C24D] transition-colors rounded-full px-7 py-3 shadow-lg shadow-[#D4AF37]/20 flex items-center gap-2 active:scale-95"
                >
                  {radioMode && isPlaying ? <><Pause size={18} /> Pause Radio</> : <><Play size={18} className="ml-0.5" /> Listen Live</>}
                </button>
                <Link
                  to="/schedule"
                  className="bg-white/5 text-white font-medium hover:bg-white/10 border border-white/10 transition-colors rounded-full px-7 py-3 flex items-center gap-2"
                >
                  <CalendarIcon size={18} /> Full Schedule
                </Link>
              </div>
            </div>

            {/* Artwork card */}
            <div className="lg:col-span-5">
              <div className="relative aspect-square rounded-3xl overflow-hidden bg-[#171A21] border border-white/10 shadow-2xl max-w-md mx-auto">
                {img ? (
                  <img src={img} alt={show.name} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <Radio size={96} className="text-[#D4AF37]/40" />
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-12 gap-10 mt-6">
        {/* When it airs */}
        <div className="lg:col-span-7">
          <div className="flex items-center gap-2 mb-6">
            <Clock size={16} className="text-[#D4AF37]" />
            <h2 className="text-xl sm:text-2xl font-semibold text-white tracking-tight" style={{ fontFamily: 'Clash Display, sans-serif' }}>
              When it airs
            </h2>
          </div>

          {entries.length === 0 ? (
            <div data-testid="show-details-no-schedule" className="bg-[#171A21] border border-white/5 rounded-2xl p-8 text-center">
              <CalendarIcon size={32} className="mx-auto text-[#94A3B8]/40 mb-3" />
              <p className="text-[#94A3B8] text-sm">This show is not currently on the weekly schedule. Check back soon.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {entries.map((e, i) => (
                <div
                  key={e.id}
                  data-testid={`show-details-slot-${i}`}
                  className="flex items-center gap-4 bg-[#171A21] border border-white/5 rounded-2xl px-5 py-4"
                >
                  <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-[#D4AF37]/20 to-[#991B1B]/20 flex items-center justify-center shrink-0">
                    <p className="text-[#D4AF37] font-mono font-bold text-sm">{DAYS[e.day_of_week].slice(0, 3)}</p>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-white font-semibold">{DAYS[e.day_of_week]}</p>
                    <p className="text-[#94A3B8] text-sm font-mono mt-0.5">
                      {e.start_time} – {e.end_time} <span className="text-[#94A3B8]/60">WAT</span>
                    </p>
                  </div>
                  <Music2 size={18} className="text-[#D4AF37]/60 shrink-0" />
                </div>
              ))}
            </div>
          )}

          <div className="mt-10">
            <AdSlot slotKey="show_details" page={`/shows/${showId}`} />
          </div>
        </div>

        {/* Host */}
        <aside className="lg:col-span-5">
          {host && host.name && (
            <div data-testid="show-details-host" className="bg-[#171A21] border border-white/5 rounded-2xl p-6 sticky top-24">
              <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#D4AF37] mb-4">Host</p>
              <div className="flex items-center gap-4 mb-4">
                {hostImg ? (
                  <img src={hostImg} alt={host.name} className="w-16 h-16 rounded-full object-cover" />
                ) : (
                  <div className="w-16 h-16 rounded-full bg-[#1F232C] flex items-center justify-center">
                    <Radio size={22} className="text-[#D4AF37]/60" />
                  </div>
                )}
                <div className="min-w-0">
                  <p className="text-white font-semibold text-lg truncate">{host.name}</p>
                  {host.bio && <p className="text-[#94A3B8] text-xs mt-0.5">On-air personality</p>}
                </div>
              </div>
              {host.bio && (
                <p className="text-[#94A3B8] text-sm leading-relaxed">{host.bio}</p>
              )}
            </div>
          )}
        </aside>
      </div>
    </div>
  );
}
